import { defineSlotRecipe } from '@chakra-ui/react';
import { inputFieldBase, inputFieldSizes } from './Input';
import { Borders, Colors, Radii, Space, Shadows, BorderWidths } from '../../types';

// Border/focus styling lives on the `root`; the inner `input` is visually transparent
// and reuses the shared input field dimensions.
const transparentStyles = {
  border: 0,
  paddingInlineStart: 'var(--input-padding)',
  _focusVisible: { ring: '0' },
  _invalid: { border: 0, ring: 0 },
};

export const numberInputSlotRecipe = defineSlotRecipe({
  slots: ['root', 'label', 'input', 'control', 'incrementTrigger', 'decrementTrigger', 'valueText', 'scrubber'],
  base: {
    root: {
      display: 'flex',
      flexDirection: 'row',
    },
    input: {
      ...inputFieldBase,
      bg: Colors.TRANSPARENT,
      borderStyle: 'none',
      boxShadow: 'none',
      borderColor: 'transparent',
    },
    control: {
      position: 'unset',
      minWidth: 'var(--input-height)',
      height: 'auto',
    },
  },
  variants: {
    variant: {
      outline: {
        root: {
          borderColor: Colors.BORDER_INPUT,
          borderWidth: BorderWidths.XS,
          borderRadius: Radii.LG,
          _hover: { borderColor: Colors.BORDER_EMPHASIS },
          _focusWithin: {
            bg: Colors.SURFACE_PAGE,
            borderColor: Colors.BORDER_FOCUS,
            outline: '2px solid',
            outlineColor: Colors.BORDER_FOCUS,
            outlineOffset: '-1px',
            '&:has(input[aria-invalid="true"])': {
              borderColor: Colors.SURFACE_ERROR,
              outlineColor: Colors.SURFACE_ERROR,
            },
          },
          '&:has(input[aria-invalid="true"])': {
            boxShadow: 'outline-error',
            borderColor: Colors.SURFACE_ERROR,
          },
        },
        input: transparentStyles,
      },
      flushed: {
        root: {
          background: Colors.TRANSPARENT,
          borderBottomColor: Colors.BORDER_DEFAULT,
          borderBottomWidth: '1px',
          _hover: { borderColor: Colors.BORDER_MUTED, borderBottomWidth: '1px' },
          _active: { borderBottomWidth: BorderWidths.SM, borderBottomColor: Colors.BORDER_EMPHASIS },
          _focusVisible: { borderBottomColor: Colors.BORDER_FOCUS, boxShadow: Shadows.OUTLINE },
        },
        input: { borderBottom: Borders.NONE },
      },
      filled: {
        root: {
          background: Colors.SURFACE_TERTIARY,
          borderColor: Colors.TRANSPARENT,
          borderWidth: '1px',
          _hover: { bg: Colors.SURFACE_TERTIARY_HOVER },
          _focusVisible: { borderColor: Colors.BORDER_FOCUS, boxShadow: Shadows.OUTLINE },
        },
        input: transparentStyles,
      },
    },
    size: {
      xs: { input: inputFieldSizes.xs },
      sm: { input: inputFieldSizes.sm },
      md: { input: inputFieldSizes.md },
      lg: { input: inputFieldSizes.lg },
    },
  },
  defaultVariants: {
    size: 'md',
    variant: 'outline',
  },
});
